import { useEffect, useRef, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowUp, Square } from "lucide-react";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onAbort: () => void;
  isLoading: boolean;
  isEditing: boolean;
  inputRef: RefObject<HTMLTextAreaElement>;
}

const MAX_HEIGHT = 200;
const LINE_HEIGHT = 23;
const PADDING = 22;
const MIN_HEIGHT = LINE_HEIGHT + PADDING;

export function ChatInput({
  value,
  onChange,
  onKeyDown,
  onSend,
  onAbort,
  isLoading,
  isEditing,
  inputRef,
}: ChatInputProps) {
  const { t } = useTranslation();

  const mountedRef = useRef(false);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;

    if (!value || value.trim() === "") {
      textarea.style.height = `${MIN_HEIGHT}px`;
      textarea.style.overflowY = "hidden";
      mountedRef.current = true;
      return;
    }

    // Reset to 0 so scrollHeight reflects actual content, not flex growth
    textarea.style.height = "0px";
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(scrollHeight, MAX_HEIGHT);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
    mountedRef.current = true;
  }, [value, inputRef]);

  return (
    <div className="flex-shrink-0 p-3 md:p-4 border-t border-border pb-safe">
      <div className="relative flex flex-col rounded-2xl border border-border/60 bg-muted/40 focus-within:border-primary/40 focus-within:bg-background transition-colors shadow-sm">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            isEditing
              ? t("pages.aiAssistant.editingPlaceholder", "Editing message... (Press Escape to cancel)")
              : t("pages.aiAssistant.inputPlaceholder", "Ask your learning coach...")
          }
          disabled={isLoading}
          rows={1}
          style={{ height: `${MIN_HEIGHT}px` }}
          className="w-full resize-none overflow-hidden border-0 bg-transparent outline-none px-4 pt-3.5 pb-2 text-sm leading-relaxed placeholder:text-muted-foreground disabled:opacity-50 max-h-[200px] overflow-y-auto"
        />
        <div className="flex items-center justify-end px-3 pb-2.5">
          {isLoading ? (
            <Button
              type="button"
              size="icon"
              variant="destructive"
              onClick={onAbort}
              className="rounded-full h-8 w-8 shrink-0"
              title={t("pages.aiAssistant.stopGeneration", "Stop generation")}
            >
              <Square className="w-3.5 h-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              type="button"
              size="icon"
              onClick={onSend}
              disabled={!value.trim() || isLoading}
              className="rounded-full h-8 w-8 shrink-0"
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
